import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { mediaKeys } from './media.queries';
import { deleteMedia, getMediaList, getMediaUrlMap, uploadMedia } from './media.requests';
import { TMediaItem, TMediaType } from './media.types';

const DEFAULT_PAGE_SIZE = 20;

export const useMediaList = (size: number = DEFAULT_PAGE_SIZE) => {
  const query = useInfiniteQuery({
    queryKey: mediaKeys.list({ size }),
    queryFn: ({ pageParam }) => getMediaList({ page: pageParam, size }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.isLastPage ? undefined : lastPage.page + 1),
  });

  const items: TMediaItem[] = query.data?.pages.flatMap((page) => page.content) ?? [];

  return {
    items,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
};

export const useMediaUrlMap = () => {
  const { data, isLoading } = useQuery({
    queryKey: mediaKeys.urlMap(),
    queryFn: getMediaUrlMap,
    staleTime: 5 * 60 * 1000,
  });

  return { mediaUrlMap: data ?? {}, isLoading };
};

export const useCreateMedia = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ file, mediaType }: { file: Blob; mediaType: TMediaType }) =>
      uploadMedia(file, mediaType, file instanceof File ? file.name : undefined),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: mediaKeys.lists() }),
  });

  return { createMedia: mutateAsync, isPending };
};

export const useDeleteMedia = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => deleteMedia(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: mediaKeys.lists() }),
  });

  return { removeMedia: mutateAsync, isPending };
};
